import React, { useEffect, useState } from "react";
import {
  Plus,
  Edit3,
  Trash2,
  Calendar,
  Heart,
  MessageCircle,
  BookOpen,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { fetchblogdata, deleteBlogApi } from "./profileAPIs";

/*----------Types---------- */
interface Blog {
  _id: string;
  title: string;
  content: string;
  excerpt: string;
  thumbnailUrl?: string;
  createdAt: string;
  likes: number;
  comments: number;
  status: "published";
}

/* ---------- Helpers ---------- */
const cleanHtml = (html?: string) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.trim() || "";
};

/* ---------- Recursive HTML Renderer ---------- */
const renderNode = (node: ChildNode, key?: string | number): React.ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;

  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;

    switch (el.tagName) {
      // Inline formatting
      case "STRONG":
      case "B":
        return <strong key={key}>{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</strong>;
      case "EM":
      case "I":
        return <em key={key}>{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</em>;
      case "U":
        return <u key={key}>{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</u>;
      case "A":
        return (
          <a
            key={key}
            href={el.getAttribute("href") || "#"}
            className="text-blue-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
          </a>
        );
      case "BR":
        return <br key={key} />;

      // Lists
      case "UL":
        return <ul key={key} className="list-disc ml-6 mb-4">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</ul>;
      case "OL":
        return <ol key={key} className="list-decimal ml-6 mb-4">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</ol>;
      case "LI":
        return <li key={key}>{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</li>;

      // Paragraphs & headings
      case "P":
        return <p key={key} className="mb-4">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</p>;
      case "H1":
        return <h1 key={key} className="text-3xl font-bold mb-4">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</h1>;
      case "H2":
        return <h2 key={key} className="text-2xl font-bold mb-4">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</h2>;
      case "H3":
        return <h3 key={key} className="text-xl font-bold mb-4">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</h3>;

      // Images
      case "IMG":
        return (
          <img
            key={key}
            src={el.getAttribute("src") || ""}
            alt={el.getAttribute("alt") || ""}
            className="my-4 w-full h-auto rounded-lg object-cover"
          />
        );

      // Blockquotes
      case "BLOCKQUOTE":
        return (
          <blockquote key={key} className="border-l-4 border-gray-300 pl-4 italic my-4 text-gray-700">
            {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
          </blockquote>
        );

      // Tables
      case "TABLE":
        return (
          <div key={key} className="overflow-x-auto mb-4">
            <table className="table-auto border border-gray-300 w-full text-left">
              {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
            </table>
          </div>
        );
      case "TR":
        return <tr key={key} className="border-b border-gray-200">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</tr>;
      case "TH":
        return (
          <th key={key} className="px-4 py-2 border border-gray-300 font-semibold bg-gray-100">
            {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
          </th>
        );
      case "TD":
        return (
          <td key={key} className="px-4 py-2 border border-gray-300">
            {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
          </td>
        );

      default:
        return Array.from(el.childNodes).map((n, i) => renderNode(n, i));
    }
  }

  return null;
};

const renderContent = (htmlString: string) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;
  return Array.from(tempDiv.childNodes).map((node, i) => renderNode(node, i));
};

/* ---------- Component ---------- */
export default function InstructorBlogsPage() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [blogToDelete, setBlogToDelete] = useState<Blog | null>(null); // for delete modal

  const navigate = useNavigate();
  const location = useLocation();
  const mentorId = location.state?.mentorId;

  /* ---------- Fetch Blogs ---------- */
  const fetchInstructorBlogs = async () => {
    setLoading(true);
    try {
      const data = await fetchblogdata(mentorId);
      if (data?.error === "Unauthorized") {
        navigate("/", { replace: true });
        return;
      }

      if (data?.blogs) {
        const formattedBlogs: Blog[] = data.blogs.map((blog: any) => ({
          _id: blog._id,
          title: blog.title || "Untitled Blog",
          content: blog.content || blog.description || "",
          excerpt: cleanHtml(blog.content || blog.description || "").slice(0, 50) + "...",
          thumbnailUrl: blog.thumbnailUrl,
          createdAt: blog.createdAt,
          likes: blog.likes ?? 0,
          comments: blog.comments ?? 0,
          status: blog.status ?? "published",
        }));

        setBlogs(formattedBlogs);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstructorBlogs();
  }, []);

  /* ---------- Delete ---------- */
  const confirmDelete = async () => {
    if (!blogToDelete) return;
    try {
      await deleteBlogApi(blogToDelete._id);
      setBlogs((prev) => prev.filter((b) => b._id !== blogToDelete._id));
      toast.success("Blog deleted successfully");
    } catch {
      toast.error("Failed to delete blog");
    } finally {
      setBlogToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading blogs...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-800">My Blogs</h1>
          </div>

          <button
            className="flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl shadow-lg"
            onClick={() => {
              if (mentorId) {
                navigate("/instructor/instructor-profile-newblogs", {
                  state: { mentorId },
                });
              }
            }}
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New Blog
          </button>
        </div>

        {/* Blogs Grid */}
        {blogs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog) => (
              <motion.div
                key={blog._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg border overflow-hidden cursor-pointer"
                onClick={() => setSelectedBlog(blog)}
              >
                <div className="h-40 bg-gradient-to-br from-purple-200 to-pink-200">
                  {blog.thumbnailUrl && (
                    <img
                      src={blog.thumbnailUrl}
                      alt={blog.title}
                      className="object-cover h-full w-full"
                    />
                  )}
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-700">
                      {blog.status}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(blog.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  <h2 className="text-lg font-bold line-clamp-2">{blog.title}</h2>

                  <p className="text-gray-600 text-sm line-clamp-3">{blog.excerpt}</p>

                  <div className="flex items-center gap-4 text-sm text-gray-500 pt-2">
                    <div className="flex items-center gap-1">
                      <Heart className="w-4 h-4" /> {blog.likes}
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" /> {blog.comments}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/instructor/edit-blog/${blog._id}`);
                      }}
                      className="flex items-center text-purple-600 text-sm font-medium"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setBlogToDelete(blog);
                      }}
                      className="flex items-center text-red-500 text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <BookOpen className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-500">No Blogs Yet</h2>
          </div>
        )}

        {/* ---------- View Blog Modal ---------- */}
        {selectedBlog && (
          <div
            className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
            style={{ zIndex: 9999 }}
            onClick={() => setSelectedBlog(null)}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl w-full h-[95vh] max-w-5xl overflow-hidden flex flex-col"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-xl font-semibold">{selectedBlog.title}</h2>
                <button
                  onClick={() => setSelectedBlog(null)}
                  className="text-gray-500 hover:text-red-500 text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="prose prose-lg max-w-none">
                  {renderContent(selectedBlog.content)}
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* ---------- Delete Confirmation Modal ---------- */}
        {blogToDelete && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-lg relative">
              <button
                onClick={() => setBlogToDelete(null)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>

              <h2 className="text-lg font-bold mb-4">Confirm Delete</h2>
              <p className="mb-6">
                Are you sure you want to delete the blog "
                <span className="font-semibold">{blogToDelete.title}</span>"? This
                action cannot be undone.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setBlogToDelete(null)}
                  className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
